import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService, BirdzService, BirdzTenantService, Profile, UserData } from '@web/birdz-angular';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, skipWhile, tap } from 'rxjs/operators';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private birdz = inject(BirdzService);
  private tenantService = inject(BirdzTenantService);

  private _user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public user$: Observable<User> = this._user$.pipe(skipWhile((u) => u == null)) as Observable<User>;
  public needConsent$: Observable<boolean> = this._user$.pipe(
    skipWhile((user) => !user),
    map((user) => user?.need_consent ?? true)
  );

  fetchUser(): Observable<User> {
    const url = `${environment.apiUrl}/user`;
    return this.http.get<UserData>(url).pipe(
      map((userData): User => {
        const user: User = new User(userData);
        this.setUser(user);
        this.auth.set(AuthService.AUTHORIZED);
        return user;
      }),
      catchError((error) => {
        const isAuthorized = [401, 403].includes(error?.status)
          ? AuthService.UNAUTHORIZED
          : AuthService.AUTHORIZATION_FAILED;
        this.auth.set(isAuthorized);
        return throwError(() => error);
      })
    );
  }

  setNeedConsent(value: boolean) {
    const user: User | null = this._user$.getValue();
    if (user === null) {
      return;
    }
    user.need_consent = value;
    this.setUser(user);
  }

  getEffectiveTenantId(): string | null {
    const user = this.getUser();
    return (user?.isBirdzAdmin() ? this.tenantService.getTenantId() : user?.getTenant()) ?? null;
  }

  grantConsent() {
    const url = `${environment.apiUrl}/me/grant-consent`;
    return this.http.post(url, null, {});
  }

  contactSupport(data: FormData): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}/support`, data);
  }

  clearCache() {
    return this.http.post(`${environment.apiUrl}/me/clear-cache`, {});
  }

  patchSettings(settings: Record<string, any>) {
    return this.http.patch(`${environment.apiUrl}/me/settings`, { settings }).pipe(
      tap((settings: Record<string, any>) => {
        // update settings
        if (settings) {
          this.setSettings(settings);
        }
      })
    );
  }

  setSettings(settings: Record<string, any>) {
    const user: User | null = this.getUser();
    if (user === null) {
      return;
    }
    user.settings = settings;
    this.setUser(user);
  }

  getUser(): User | null {
    return this._user$.getValue();
  }

  setUser(user: User): void {
    this.birdz.configureFor(user);
    this._user$.next(user);
  }

  isBirdzAdmin(): boolean {
    return this.getUser()?.isBirdzAdmin() ?? false;
  }

  getShowRawKpis(): boolean {
    return this.getUser()?.preferences?.['show_raw_kpis'] ?? false;
  }

  patchPreferences(preferences: Record<string, any>) {
    return this.http.patch(`${environment.apiUrl}/me/preferences`, { preferences }).pipe(
      tap((preferences) => {
        if (preferences) {
          this.setPreferences(preferences);
        }
      })
    );
  }

  setPreferences(preferences: Record<string, any>) {
    const user = this.getUser();
    if (user) {
      user.preferences = preferences;
      this.setUser(user);
    }
  }

  updateUserAvatar(file: File): Observable<Profile> {
    const body = new FormData();
    body.append('avatar', file, file.name);

    return this.http.post<Profile>(`${environment.apiUrl}/me/avatar`, body);
  }
}
